import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {minimizeCommentMutation as minimizeCommentMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  MinimizeCommentMutationMutation,
  ReportedContentClassifiers,
} from '@github-ui/server-persisted-queries/types/minimizeCommentMutation'
import {type JSONRequestInit, reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

function minimizationPath(owner?: string, repo?: string): string {
  if (owner && repo) {
    return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
  }

  const [, inferredOwner, inferredRepo] = window.location.pathname.split('/')
  if (!inferredOwner || !inferredRepo) {
    throw new Error('Failed to infer repository path for minimizing comment')
  }

  return `/${encodeURIComponent(inferredOwner)}/${encodeURIComponent(inferredRepo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
}

type MinimizeOperation = SetFieldsOperation & {
  minimizeInput: {
    reason: ReportedContentClassifiers
    owner?: string
    repo?: string
    onCompleted?: () => void
    onError?: (error?: Error) => void
  }
}

type MinimizeResult = {
  isMinimized: boolean
  minimizedReason: string | null
}

async function submitMinimizeComment(operation: SetFieldsOperation): Promise<MinimizeResult> {
  const {id, minimizeInput} = operation as MinimizeOperation
  const {reason, owner, repo} = minimizeInput
  const request: JSONRequestInit = {
    method: 'PUT',
    body: {input: {subjectId: id, classifier: reason}},
  }
  const response = await reactFetchJSON(minimizationPath(owner, repo), request)
  if (!response.ok) throw new Error(`Failed to minimize comment (${response.status})`)

  const payload = (await response.json()) as {
    errors?: Array<{message?: string}>
    data?: MinimizeCommentMutationMutation
  }
  const comment = payload.data?.minimizeComment?.minimizedComment
  if (!comment) throw new Error(payload.errors?.[0]?.message ?? 'Failed to minimize comment')
  return {isMinimized: comment.isMinimized, minimizedReason: comment.minimizedReason ?? null}
}

export function useMinimizeCommentMutation() {
  const mutation = useEntityMutation<MinimizeResult>({
    mutationFn: submitMinimizeComment,
    getConfirmedOperation: (result, operation) => ({
      type: 'setFields',
      id: operation.id,
      fields: {
        isHidden: result.isMinimized,
        minimizedReason: result.minimizedReason,
        pendingMinimizeReason: result.minimizedReason,
      },
    }),
    onSuccess: (_result, {operation}) => {
      const {minimizeInput} = operation as MinimizeOperation
      minimizeInput.onCompleted?.()
    },
    onError: (error, {operation}) => {
      ;(operation as MinimizeOperation).minimizeInput.onError?.(error)
    },
  })

  return useCallback(
    ({
      input: {id, reason, owner, repo},
      onCompleted,
      onError,
    }: {
      input: {id: string; reason: ReportedContentClassifiers; owner?: string; repo?: string}
      onCompleted?: () => void
      onError?: (error?: Error) => void
    }) => {
      const operation: MinimizeOperation = {
        type: 'setFields',
        id,
        fields: {isHidden: true, minimizedReason: reason, pendingMinimizeReason: reason},
        minimizeInput: {reason, owner, repo, onCompleted, onError},
      }
      try {
        mutation.mutate({operation})
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Failed to minimize comment'))
      }
    },
    [mutation],
  )
}
